import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const servicesRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const serviceLinks = [
    { name: 'Window Cleaning', href: '/services/window-cleaning' },
    { name: 'House & Roof Washing', href: '/services/house-washing' },
    { name: 'Surface Cleaning', href: '/services/surface-cleaning' },
    { name: 'Gutter Cleaning', href: '/services/gutter-cleaning' },
    { name: 'Shrink Wrapping', href: '/services/shrink-wrapping' },
    { name: 'Commercial Cleaning', href: '/services/commercial-cleaning' },
    { name: 'Interior Cleaning', href: '/services/interior-cleaning' }
  ];

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    if (location.pathname === '/') {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      window.location.href = '/' + sectionId;
    }
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Row - Logo left, CTA right */}
        <div className="flex justify-between items-center h-20 sm:h-24 border-b border-gray-100">
          {/* Logo - Left aligned */}
          <div className="flex-shrink-0">
            <Link to="/">
              <img
                src="https://customer-assets.emergentagent.com/job_034ba03a-ab9b-460a-8d65-ab113159d799/artifacts/9u4lf7am_logo%20transparency-01.png"
                alt="Reflections Cleanings"
                className="h-16 sm:h-20 w-auto"
                data-testid="navbar-logo"
              />
            </Link>
          </div>

          {/* Phone + CTA - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:6316608164"
              className="flex flex-col items-end"
              data-testid="navbar-phone"
            >
              <span className="text-blue-600 font-bold text-lg">(631) 660-8164</span>
              <span className="text-gray-500 text-xs">Call or text now for an estimate</span>
            </a>
            <Link
              to="/request-a-quote"
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 shadow-md whitespace-nowrap text-sm"
              data-testid="navbar-quote-btn"
            >
              Request a Quote
            </Link>
          </div>

          {/* Mobile - Phone + Quote + Menu */}
          <div className="flex items-center gap-2 lg:hidden">
            <a href="tel:6316608164" className="text-blue-600 font-bold text-xs sm:text-sm" data-testid="navbar-phone-mobile">
              (631) 660-8164
            </a>
            <Link
              to="/request-a-quote"
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg font-semibold text-xs whitespace-nowrap"
              data-testid="navbar-quote-btn-mobile"
            >
              Get a Quote
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-blue-600 transition-colors"
              data-testid="mobile-menu-btn"
            >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Bottom Row - Navigation Links */}
        <div className="hidden lg:flex items-center justify-center space-x-8 py-2.5">
          <Link
            to="/"
            className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="nav-home"
          >
            Home
          </Link>

          {/* Services Dropdown */}
          <div
            ref={servicesRef}
            className="relative"
            onMouseEnter={() => setIsServicesOpen(true)}
            onMouseLeave={() => setIsServicesOpen(false)}
          >
            <button
              className="flex items-center gap-1 text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
              data-testid="nav-services-dropdown"
            >
              Services
              <ChevronDown className={`w-4 h-4 transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
            </button>

            {isServicesOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50">
                <div className="bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[220px]">
                  {serviceLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      onClick={() => setIsServicesOpen(false)}
                      className="block px-5 py-2.5 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm font-medium"
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={() => scrollToSection('#about')}
            className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="nav-about"
          >
            About
          </button>

          <button
            onClick={() => scrollToSection('#testimonials')}
            className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="nav-testimonials"
          >
            Testimonials
          </button>

          <button
            onClick={() => scrollToSection('#contact')}
            className="text-gray-700 hover:text-blue-600 transition-colors text-sm font-medium"
            data-testid="nav-contact"
          >
            Contact
          </button>

          <Link
            to="/request-a-quote"
            className="text-red-500 hover:text-red-600 transition-colors text-sm font-semibold italic"
            data-testid="nav-request-quote"
          >
            Request a Quote
          </Link>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
            >
              Home
            </Link>

            {/* Mobile Services Accordion */}
            <button
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
              className="flex items-center justify-between w-full px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
            >
              Services
              <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} />
            </button>

            {isMobileServicesOpen && (
              <div className="pl-4 space-y-1">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2.5 text-gray-600 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            )}

            <button
              onClick={() => { scrollToSection('#about'); }}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
            >
              About
            </button>

            <button
              onClick={() => { scrollToSection('#testimonials'); }}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
            >
              Testimonials
            </button>

            <button
              onClick={() => { scrollToSection('#contact'); }}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors font-medium"
            >
              Contact
            </button>

            <Link
              to="/request-a-quote"
              onClick={() => setIsMobileMenuOpen(false)}
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors mt-2 text-center"
            >
              Request a Quote
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
